import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import * as parseDMS from 'parse-dms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { SearchApi } from '../../../api/search-api';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter, JSONConverter } from '../../../inputConverter';
import { GeocodeService } from '../../../services/geocode.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { IMCMapView, IQuery, ISearchResult, ISearchResultType } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-search',
    styleUrls: ['../../sharedStyles/base.scss', './search.component.scss'],
    templateUrl: './search.component.html'
})
export class SearchComponent extends BaseComponent implements AfterViewInit {
    @Input() addressSymbolDefinition: any = {
        type: 'simple-marker',
        style: 'circle',
        color: 'yellow',
        size: '16px'
    };
    @Input() @InputConverter() addSelectionToMap: boolean = false;
    @Input() maxResults: number = 100;
    @Input() debounceTimer: number = 1000;
    @Input() @InputConverter(BooleanConverter) filterLayersBeforeAdd = false;
    @Input() mapViewRef: IMCMapView;
    @Input() placeHolder: string = 'Enter search here...';
    @Input() text: string = '';
    @Input() selectedItem: ISearchResult;
    @Input() selectedTypeName: string = 'feature';
    @Input() combinedResults: ISearchResult[];
    @Input() results: { [key: string]: ISearchResult[] } = {};
    @Input() user: any = null;
    @Input() @InputConverter(JSONConverter) excludeTypes: string[] = [];
    @Input() @InputConverter(BooleanConverter) enableNotificationSearch = false;

    hasFocus: boolean;
    loading: boolean = false;
    error: boolean = false;
    graphicsLayer: __esri.GraphicsLayer;
    coordinateFormatter: __esri.coordinateFormatter;
    point: any = null;
    searchUpdated = new Subject<string>();
    typeResults: ISearchResultType[] = [];

    @Output() readonly onresultselected: EventEmitter<{ type: string; result: any }> = new EventEmitter<{ type: string; result: any }>();

    constructor(_vcr: ViewContainerRef,
        @Inject('authenticationService') private readonly authService: IAuthenticationService,
        private readonly geocodeService: GeocodeService,
        private readonly layerService: LayerService,
        private readonly mapService: MapService,
        private readonly searchApi: SearchApi,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(_vcr, true);
        this.registerEvent('onresultselected', this.onresultselected);
        this.searchUpdated
            .pipe(
                debounceTime(this.debounceTimer)
            )
            .subscribe(() => {
                this.search();
            });
    }

    async ngAfterViewInit() {
        this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
        this.mapService.mcMapView = this.mapViewRef;
        this.authService.user.subscribe(user => {
            this.user = user;
        });
        await this.loadCoordinateFormatter();
    }

    async loadCoordinateFormatter() {
        if (!this.coordinateFormatter) {
            this.coordinateFormatter = await ESRI.CoordinateFormatter;
            if (!this.coordinateFormatter.isLoaded()) {
                await this.coordinateFormatter.load();
            }
        }
    }

    @HostListener('document:click', ['$event'])
    documentClick(event: any) {
        if (!event.target || event.target.tagName !== 'MC-SEARCH') {
            this.hasFocus = false;
        }
    }

    getTypeCount(type: string) {
        return this.results[type].length;
    }

    textboxClick() {
        if (!this.hasFocus) {
            this.hasFocus = true;
            this.search();
        }
    }

    selectType(typeName: string) {
        this.selectedTypeName = typeName;
    }

    getPluralType(type: string) {
        const firstLetter = type.substring(0, 1).toUpperCase();
        const rest = type.substring(1);
        return `${firstLetter}${rest}s`;
    }

    async onTextboxChange() {
        this.searchUpdated.next(this.text);
    }

    dontClear = false;
    async search() {
        this.loading = false;
        this.error = false;
        this.hasFocus = true;
        this.dontClear = false;
        this.point = null;
        if (this.text.length && !this.text.includes('|') && !this.text.includes('/')) {
            try {
                const latLong = parseDMS(this.text);
                if (latLong && latLong.lat && latLong.lon) {
                    await this.loadCoordinateFormatter();
                    this.point = this.coordinateFormatter.fromLatitudeLongitude(`${latLong.lat} ${latLong.lon}`);
                    if (this.point) {
                        this.dontClear = true;
                        this.typeResults = [];
                        this.results = {};
                        const tempTypeResults = {
                            name: 'feature',
                            pluralDisplayName: 'Features',
                            count: 1
                        } as ISearchResult;
                        this.typeResults.push(tempTypeResults);
                        this.cdr.detectChanges();
                    }
                }
            } catch { }
        }
        this.normalTextSearch();
    }

    async showLatLongOnMap() {
        const Graphic = await ESRI.Graphic;
        const pointGraphic = new Graphic({
            geometry: this.point as any,
            symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: 'yellow',
                size: '16px'
            } as any
        });
        const pointName = `Point:   ${this.point.latitude}  ,  ${this.point.longitude}`;
        await this.addGraphicAndZoomToIt(pointGraphic, pointName);
        this.text = '';
        this.typeResults = [];
        this.point = null;
    }

    cancelPointOnMap() {
        this.typeResults = [];
        this.text = '';
        this.point = null;
    }

    private async addGraphicAndZoomToIt(pointGraphic: any, title: string) {
        if (!this.graphicsLayer || !(<any>this.graphicsLayer).parent) {
            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title });
        }
        this.graphicsLayer.add(pointGraphic);
        this.layerService.zoomToGraphic(pointGraphic);
    }

    normalTextSearch() {
        let validSearch = true;
        if (this.text.length > 0) {
            const endChar = this.text.substr(-1);
            if (endChar === ' ' || endChar === ',') {
                validSearch = false;
            }
            if (validSearch) {
                this.retrieveResults();
            }
        } else {
            this.clearResults();
        }
    }

    async retrieveResults() {
        this.loading = true;
        if (!this.dontClear) {
            this.typeResults = [];
        }
        this.cdr.detectChanges();
        try {
            const textForThisSearch = this.text;
            let userId = null;
            if (this.user) {
                userId = this.user.id;
            }
            if (typeof this.maxResults !== 'number') {
                this.maxResults = parseInt(this.maxResults, 10);
            }
            const newResults = await this.searchApi.getResults(textForThisSearch, userId, '', this.maxResults, this.excludeTypes, environment.appId);
            if (textForThisSearch === this.text) {
                this.results = this.filterResults(newResults);
                if (this.results && this.results.feature && this.results.feature.length && this.results.feature.length > 0) {
                    // Group results by layer title, using a Map to preserve the layer ordering.
                    const featuresByGroup = new Map<string, ISearchResult[]>();

                    for (const result of this.results.feature) {
                        if (!featuresByGroup.has(result.layerTitle)) {
                            featuresByGroup.set(result.layerTitle, []);
                        }

                        featuresByGroup.get(result.layerTitle).push(result);
                    }

                    // Sort each group alphabetically.
                    this.results.feature = [];

                    for (const groupFeatures of featuresByGroup.values()) {
                        groupFeatures.sort((a, b) => a.primaryText.localeCompare(b.primaryText));
                        this.results.feature = this.results.feature.concat(groupFeatures);
                    }
                }
                this.loading = false;
                this.setTypeResults();
                this.addWholeRouteResult();
            }
        } catch (er) {
            console.error(er);
            this.loading = false;
            this.error = true;
        }
        this.cdr.detectChanges();
    }

    filterResults(results: { [key: string]: ISearchResult[] }): { [key: string]: ISearchResult[] } {
        for (const type of this.searchApi.getSearchResultTypes(this.excludeTypes)) {
            const newList: ISearchResult[] = [];
            const uninqueKeys: string[] = [];
            for (const result of results[type.name]) {
                if (result.tertiaryText) {
                    if (result.tertiaryText.toUpperCase().indexOf('NOCO') > -1) {
                        result.layerTitle += ' - NOCO';
                    } else if (result.tertiaryText.toUpperCase().indexOf('PAYROLL') > -1) {
                        result.layerTitle += ' - Payroll';
                    }
                }
                const uninqueKey = `${result.layerTitle}_${result.primaryText}_${result.layerId}`;
                if (uninqueKeys.indexOf(uninqueKey) < 0) {
                    uninqueKeys.push(uninqueKey);
                    newList.push(result);
                }
                const idx = uninqueKeys.indexOf(uninqueKey);
                if (idx > -1) {
                    if (!newList[idx].objectIds) {
                        newList[idx].objectIds = [];
                    }
                    newList[idx].objectIds.push(result.objectId);
                }
            }
            results[type.name] = newList;
            if (results[type.name].length > this.maxResults) {
                results[type.name].length = this.maxResults;
            }
        }
        return results;
    }

    setTypeResults() {
        if (!this.dontClear) {
            this.typeResults = [];
        }
        for (const type of this.searchApi.getSearchResultTypes(this.excludeTypes)) {
            let typeWithCount = this.typeResults.find(x => x.name === type.name);
            if (typeWithCount) {
                typeWithCount.count += this.results[type.name].length;
            } else {
                typeWithCount = { ...type, ...{} };
                typeWithCount.count = this.results[type.name].length;
                this.typeResults.push(typeWithCount);
            }
        }
        this.selectedTypeName = this.typeResults.find(results => !!results.count) ? this.typeResults.find(results => !!results.count).name : '';
    }

    addWholeRouteResult() {
        if (this.typeResults && this.typeResults.some(tr => tr.name === 'feature' && tr.count > 0)) {
            const featureResults = this.results.feature;
            const rmsLayers = environment.searchReplacement.find(sr => sr.replacementFormat.toLowerCase().startsWith('rms'));
            if (rmsLayers) {
                const firstResultIndex = featureResults.findIndex(r => rmsLayers.layerIds.includes(r.layerId));
                if (firstResultIndex >= 0) {
                    const firstResult = featureResults[firstResultIndex];
                    const crsParts = firstResult.primaryText.split('|');
                    featureResults.splice(firstResultIndex, 0, {
                        layerId: firstResult.layerId,
                        layerTitle: firstResult.layerTitle,
                        type: firstResult.type,
                        primaryText: crsParts.filter((_x, i, a) => i < a.length - 1).join('|'),
                        secondaryText: firstResult.secondaryText,
                        tertiaryText: `SR ${crsParts[1]}`
                    });
                }
            }
        }
    }

    async addResultToMap(result: ISearchResult) {
        this.selectedItem = result;
        this.hasFocus = false;
        if (this.addSelectionToMap) {
            this.onTextboxChange();
            this.clearResults();
            let res: any = await this.addResultToMapBasedOnType(result);
            if (!res) {
                res = result;
            }
            this.onresultselected.emit({ type: result.type, result: res });
            this.text = '';
        }
    }

    /* tslint:disable:cyclomatic-complexity */
    async addResultToMapBasedOnType(result: ISearchResult) {
        let name = result.primaryText;
        let sapNotif = result.primaryText;
        let notifNumber = null;
        if (this.enableNotificationSearch && environment.searchReplacement.filter(x => x.layerIds.includes(result.layerId))[0]) {
            notifNumber = result.primaryText.match(/\d+/);
            if (notifNumber) {
                sapNotif = result.primaryText.substring(notifNumber.index);
            }
        }
        let nocoPayroll = '';
        if (result.tertiaryText) {
            if (result.tertiaryText.toUpperCase().indexOf('NOCO') > -1) {
                nocoPayroll = ' NOCO';
            } else if (result.tertiaryText.toUpperCase().indexOf('PAYROLL') > -1) {
                nocoPayroll = ' Payroll';
            }
        }
        if (result.layerTitle) {
            const data = environment.searchReplacement.filter(x => x.layerIds.includes(result.layerId));
            if (this.enableNotificationSearch && data && data.length && environment.sapLayerIds.some(item => data[0].layerIds.includes(item))) {
                name = data[0].replacementFormat.replace('{sapNotif}', 'Notification ' + sapNotif).replace('{sapStatus}', data[0].sapStatus ? data[0].sapStatus : '').replace('{nocoPayroll}', nocoPayroll ? ('by' + nocoPayroll) : '');
            } else if (data && data.length && data[0].replacementFormat.includes('{primaryText}')) {
                name = data[0].replacementFormat.replace('{primaryText}', result.primaryText);
            } else {
                name = result.layerTitle;
            }
        }
        switch (result.type) {
            case 'address':
                await this.addGraphicForAddressResult(result);
                break;
            case 'map':
                const maps = await this.mapService.getMaps([result.id]);
                const map = maps[0];
                await this.mapService.openMap(map);
                return map;
                break;
            case 'layer':
                const layerSettings = { id: result.id, name, zoomToLayer: true };
                if (this.filterLayersBeforeAdd) {
                    window.dispatchEvent(new CustomEvent('addingMapcoreLayersToMap', { detail: [layerSettings], composed: true }));
                } else {
                    return await this.layerService.addMapcoreLayersToMap([layerSettings]);
                }
                break;
            case 'feature':
                let query: IQuery;
                if (result.objectIds && result.objectIds.length > 1) {
                    query = { field: 'objectid', operator: 'IN', value: result.objectIds.toString() };
                } else if (result.objectId) {
                    query = { field: 'objectid', operator: '=', value: result.objectId.toString() };

                }
                if (result.primaryText.indexOf(': ') > 0) {
                    const parts = result.primaryText.split(': ');
                    const searchKeys = environment.featureSearchKeys.filter(x => parts[0] === x.prefix);
                    if (searchKeys.length > 0 && parts.length > 1) {
                        const searchKey = searchKeys[0];
                        query.field = searchKey.column;
                        query.value = parts[1].trim();
                    }
                } else if (result.primaryText.indexOf('|') > 0) {
                    const searchKeys = environment.featureSearchKeys.filter(x => x.pipes);
                    if (searchKeys.length > 0) {
                        const values = result.primaryText.split('|');
                        query = { operator: 'AND', subqueries: [] };
                        const searchKey = searchKeys[0];
                        for (let i = 0; i < searchKey.columns.length; i++) {
                            if (values[i]) {
                                const subquery = { field: searchKey.columns[i], operator: '=', value: values[i] };
                                query.subqueries.push(subquery);
                            }
                        }
                    }
                }
                const featureLayerSettings = { id: result.layerId, name, query, zoomToLayer: true, identifyEnabled: true };
                return await this.layerService.addMapcoreLayersToMap([featureLayerSettings]);

                break;
            default:
                break;
        }
    }

    async addGraphicForAddressResult(result: ISearchResult) {
        const findResult = await this.geocodeService.find(result.name, result.magicKey).toPromise();
        if (findResult.candidates.length > 0) {
            const Graphic = await ESRI.Graphic;
            const candidate = findResult.candidates[0];
            const point = {
                type: 'point',
                x: candidate.location.x,
                y: candidate.location.y,
                spatialReference: findResult.spatialReference
            };
            const pointGraphic = new Graphic({
                geometry: point,
                symbol: this.addressSymbolDefinition
            });
            const graphicsLayer = await this.layerService.addGraphicsLayer({ title: candidate.address }, { identifyEnabled: true });
            graphicsLayer.add(pointGraphic);
            await this.layerService.zoomToGraphic(pointGraphic);
        }
    }

    clearResults() {
        this.results = {};
        this.typeResults = [];
    }
}
